import { useTheme } from '@/context/ThemeContext';
import { WikiSettings } from '@/context/WikiContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

interface Props {
  settings: WikiSettings;
  activeId: string;
  onSelect: (id: string) => void;
}

export default function WikiHero({ settings, activeId, onSelect }: Props) {
  const { theme } = useTheme();
  const { settings: badgeSettings } = useBadgeVisibility();
  const light = theme === 'light';

  const published = settings.articles.filter((a) => a.published);

  return (
    <header className={`pt-28 pb-16 w-full px-6 md:px-10 ${light ? 'bg-gray-50' : 'bg-[#0D1B2A]'} transition-colors duration-300`}>
      <div className="max-w-5xl mx-auto text-center">
        {badgeSettings.wikiBadges && (
          <span
            className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5"
            style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', color: 'var(--accent)' }}
          >
            Knowledge Base
          </span>
        )}
        <h1 className={`text-4xl md:text-5xl font-black mb-4 ${light ? 'text-gray-900' : 'text-white'}`}>
          {settings.pageTitle}
        </h1>
        <p className={`text-lg max-w-2xl mx-auto mb-10 ${light ? 'text-gray-500' : 'text-white/50'}`}>
          {settings.pageSubtitle}
        </p>

        {/* Tab selector */}
        <div className={`inline-flex gap-2 p-1.5 rounded-2xl ${light ? 'bg-white border border-gray-200' : 'bg-white/5 border border-white/10'}`}>
          {published.map((article) => (
            <button
              key={article.id}
              onClick={() => onSelect(article.id)}
              className={`flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeId === article.id
                  ? 'text-white shadow-lg'
                  : light
                  ? 'text-gray-500 hover:text-gray-800'
                  : 'text-white/40 hover:text-white/70'
              }`}
              style={activeId === article.id ? { background: article.badgeColor } : {}}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={article.icon}></i>
              </div>
              {article.title}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
